import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getOnlineStatusSubscription, isOnline as probeOnlineStatus } from '@ombori/grid-signals-react';
import useGridMobileInstallationResumedListener from './grid-mobile/use-grid-mobile-installation-resumed-listener';

const OnlineReactContext = React.createContext({
  isOnline: true,
});

interface OnlineContextProviderProps {
  children?: ReactNode;
  /** Only tests pass these; production uses the exported constants. */
  offlineConfirmDelayMs?: number;
  browserOfflineConfirmDelayMs?: number;
  recoveryProbeIntervalMs?: number;
}

/**
 * The shared status check polls every 10 s but only emits when the result flips,
 * so a single "offline" emission is all we ever get for an outage. Counting
 * emissions could therefore never reach "two failed checks in a row" and the app
 * never went offline. Instead an offline emission starts this timer; if nothing
 * reports online before it fires (one more poll interval plus margin), the app
 * goes offline. Online is immediate so recovery (and the refetch it triggers) is
 * not delayed.
 */
export const OFFLINE_CONFIRM_DELAY_MS = 15 * 1000;

/**
 * The browser's own "offline" event is reliable but fires on every interface
 * blip (access-point roaming, Wi-Fi to cellular). Going offline tears the live
 * connection down and recovery reloads every ticket, so a blip shorter than
 * this never costs anything.
 */
export const BROWSER_OFFLINE_CONFIRM_DELAY_MS = 5 * 1000;

/**
 * While offline the shared poll cannot bring the app back if it never saw the
 * gap itself (it emits only on change), so the app probes on its own until a
 * request gets through.
 */
export const RECOVERY_PROBE_INTERVAL_MS = 10 * 1000;

const globalOnlineStatusSubscription = getOnlineStatusSubscription();

export const OnlineContextProvider = ({
  children,
  offlineConfirmDelayMs = OFFLINE_CONFIRM_DELAY_MS,
  browserOfflineConfirmDelayMs = BROWSER_OFFLINE_CONFIRM_DELAY_MS,
  recoveryProbeIntervalMs = RECOVERY_PROBE_INTERVAL_MS,
}: OnlineContextProviderProps) => {
  const [onlineContextValue, setOnlineContextValue] = useState({ isOnline: true });
  const { lastGridMobileResumedTimestamp } = useGridMobileInstallationResumedListener();
  const offlineTimer = useRef<ReturnType<typeof setTimeout>>();
  const isProbing = useRef(false);

  const setIsOnline = useCallback((isOnline: boolean) => {
    setOnlineContextValue((current) => (current.isOnline === isOnline ? current : { isOnline }));
  }, []);

  const clearOfflineTimer = useCallback(() => {
    if (offlineTimer.current) {
      clearTimeout(offlineTimer.current);
      offlineTimer.current = undefined;
    }
  }, []);

  const goOnline = useCallback(() => {
    clearOfflineTimer();
    setIsOnline(true);
  }, [clearOfflineTimer, setIsOnline]);

  // Go offline unless something reports online before the delay. A shorter
  // delay already pending is kept.
  const goOfflineUnlessRecovered = useCallback(
    (delayMs: number) => {
      if (offlineTimer.current) {
        return;
      }
      offlineTimer.current = setTimeout(() => {
        offlineTimer.current = undefined;
        setIsOnline(false);
      }, delayMs);
    },
    [setIsOnline],
  );

  // One real request; the app goes online only if it gets through. "online"
  // from the browser only means an interface is back, not that anything is
  // reachable, and going online is the expensive transition.
  const verifyOnline = useCallback(() => {
    if (isProbing.current) {
      return;
    }
    isProbing.current = true;
    probeOnlineStatus()
      .then((reachable) => {
        if (reachable) {
          goOnline();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        isProbing.current = false;
      });
  }, [goOnline]);

  useEffect(() => {
    globalOnlineStatusSubscription.start();

    return () => {
      globalOnlineStatusSubscription.stop();
    };
  }, [lastGridMobileResumedTimestamp]);

  useEffect(() => {
    const unsubscribe = globalOnlineStatusSubscription.subscribe((isOnline) => {
      if (isOnline) {
        goOnline();
      } else {
        goOfflineUnlessRecovered(offlineConfirmDelayMs);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [goOnline, goOfflineUnlessRecovered, offlineConfirmDelayMs]);

  useEffect(() => {
    const onBrowserOffline = () => goOfflineUnlessRecovered(browserOfflineConfirmDelayMs);

    window.addEventListener('offline', onBrowserOffline);
    window.addEventListener('online', verifyOnline);

    return () => {
      window.removeEventListener('offline', onBrowserOffline);
      window.removeEventListener('online', verifyOnline);
      clearOfflineTimer();
    };
  }, [goOfflineUnlessRecovered, browserOfflineConfirmDelayMs, verifyOnline, clearOfflineTimer]);

  // Recovery loop: while offline, probe on our own until a request gets through.
  useEffect(() => {
    if (onlineContextValue.isOnline) {
      return undefined;
    }
    const interval = setInterval(verifyOnline, recoveryProbeIntervalMs);

    return () => {
      clearInterval(interval);
    };
  }, [onlineContextValue.isOnline, verifyOnline, recoveryProbeIntervalMs]);

  return <OnlineReactContext.Provider value={onlineContextValue}>{children}</OnlineReactContext.Provider>;
};

export const useOnlineContext = () => {
  return useContext(OnlineReactContext);
};
